<template>
  <div class="space-y-5">
    <div class="space-y-1">
      <label
        for="email"
        class="block text-sm font-semibold text-gray-700 mb-2"
      >
        <MailIcon class="w-4 h-4 inline mr-2 text-gray-500" />
        Email Address
      </label>
      <div class="relative">
        <input
          id="email"
          type="email"
          v-model="email"
          class="w-full px-4 py-3 pl-11 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-300"
          placeholder="your@email.com"
        />
        <MailIcon
          class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
        />
      </div>
    </div>

    <div class="space-y-1">
      <label
        for="password"
        class="block text-sm font-semibold text-gray-700 mb-2"
      >
        <LockIcon class="w-4 h-4 inline mr-2 text-gray-500" />
        Password
      </label>
      <div class="relative">
        <input
          id="password"
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          class="w-full px-4 py-3 pl-11 pr-11 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-300"
          placeholder="Enter your password"
        />
        <LockIcon
          class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
        />
        <button
          type="button"
          @click="showPassword = !showPassword"
          class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <EyeIcon
            v-if="!showPassword"
            class="w-5 h-5"
          />
          <EyeOffIcon
            v-else
            class="w-5 h-5"
          />
        </button>
      </div>
    </div>

    <transition
      name="error"
      mode="out-in"
    >
      <div
        v-if="error"
        class="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3"
      >
        <AlertCircleIcon class="w-4 h-4 flex-shrink-0" />
        <span>{{ error }}</span>
      </div>
    </transition>

    <div class="flex items-center justify-between text-sm">
      <label class="flex items-center cursor-pointer group">
        <input
          type="checkbox"
          v-model="rememberMe"
          class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 transition-colors"
        />
        <span
          class="ml-2 text-gray-600 group-hover:text-gray-800 transition-colors"
          >Remember me</span
        >
      </label>
      <RouterLink
        to="/forgot-password"
        class="text-blue-600 hover:text-blue-800 font-semibold hover:underline transition-colors"
      >
        Forgot password?
      </RouterLink>
    </div>

    <div>
      <button
        @click="handleSubmit"
        :disabled="isLoading"
        class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
      >
        <LoaderIcon
          v-if="isLoading"
          class="w-5 h-5 animate-spin"
        />
        <LogInIcon
          v-else
          class="w-5 h-5"
        />
        <span>{{ isLoading ? "Signing In..." : "Sign In" }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ref } from "vue";
  import {
    MailIcon,
    LockIcon,
    EyeIcon,
    EyeOffIcon,
    LogInIcon,
    LoaderIcon,
    AlertCircleIcon,
  } from "lucide-vue-next";

  defineProps({
    error: {
      type: String,
      default: "",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["submit", "update:error"]);

  const email = ref("");
  const password = ref("");
  const showPassword = ref(false);
  const rememberMe = ref(false);

  const handleSubmit = () => {
    if (!email.value || !password.value) {
      emit("update:error", "Please fill in all fields");
      return;
    }

    emit("submit", {
      email: email.value,
      password: password.value,
      rememberMe: rememberMe.value,
    });
  };
</script>

<style scoped>
  .error-enter-active,
  .error-leave-active {
    transition: all 0.3s ease-out;
  }

  .error-enter-from,
  .error-leave-to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }

  input:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
</style>
